/*
 * Copyright (c) 2024. World Wide Generation Ltd
 */

import React, { useState, useEffect, useMemo } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  useListGenerationRunsQuery,
  useGetRunWithInsightsQuery,
  useCreateGenerationRunMutation,
} from '@api/insights-synthesis';
import { GenerationRunStatus } from '@g17eco/types/insights-synthesis';
import { useInsightsFilters } from './hooks/useInsightsFilters';
import { DashboardRow } from '../../../../molecules/dashboard';
import { Header } from './partials/Header';
import { BlockingLoader } from '../../../../atoms/loader/BlockingLoader';
import { Loader } from '../../../../atoms/loader/Loader';
import { Alert } from 'reactstrap';
import { getIntelligenceViewDetails, getRunLabel, IntelligenceView } from './utils/constants';
import { Heading } from './partials/Heading';
import { InsightsFilters } from './partials/InsightsFilters';
import { IntelligenceView as IntelligenceViewComponent } from './IntelligenceView';
import { PrioritiesView } from './PrioritiesView';
import { RecommendationsView } from './RecommendationsView';
import { filterInsights } from './utils/helpers';
import { toastFromError } from '../../../../molecules/toasts';

interface IntelligenceContainerProps {
  initiativeId: string;
}

const POLL_INTERVAL_MS = 5 * 1000;

export const IntelligenceContainer: React.FC<IntelligenceContainerProps> = ({ initiativeId }) => {
  const [view, setView] = useState<IntelligenceView>(IntelligenceView.Intelligence);
  const { filters, setFilters } = useInsightsFilters();
  const [selectedBatchId, setSelectedBatchId] = useState<string | undefined>();
  const [isPolling, setIsPolling] = useState(false);

  const { data: runs, isLoading: isRunsLoading } = useListGenerationRunsQuery({
        initiativeId,
        status: [
          GenerationRunStatus.Pending,
          GenerationRunStatus.Processing,
          GenerationRunStatus.Completed,
          GenerationRunStatus.Failed,
        ],
      },
    {
      // Poll only when selected run is Pending (and not timed out). Use ref from previous render.
      pollingInterval: isPolling ? POLL_INTERVAL_MS : 0,
    },
  );

  useEffect(() => {
    // Always select the latest run if no run is selected
    if (!selectedBatchId && runs && runs.length > 0) {
      setSelectedBatchId(runs[0].batchId);
    }
  }, [selectedBatchId, runs]);

  const { data: selectedRun, isFetching: isRunFetching } = useGetRunWithInsightsQuery(
    selectedBatchId && initiativeId ? { initiativeId, batchId: selectedBatchId } : skipToken,
    {
      // Poll only when selected run is Pending (and not timed out). Use ref from previous render.
      pollingInterval: isPolling ? POLL_INTERVAL_MS : 0,
    },
  );

  useEffect(() => {
    setIsPolling(selectedRun?.status === GenerationRunStatus.Processing);
  }, [selectedRun]);

  const [createGenerationRun, { isLoading: isGenerating }] = useCreateGenerationRunMutation();

  const handleGenerateInsights = async () => {
    try {
      const result = await createGenerationRun({
        initiativeId,
        data: { clientId: initiativeId },
      }).unwrap();

      setSelectedBatchId(result.batchId); // Set to polling or stop if failed
    } catch (error) {
      toastFromError({ error, title: 'Failed to generate insights' });
      setSelectedBatchId(undefined);
    }
  };

  // Pre-compute all derived state for Dashboard
  const runOptions = useMemo(() => {
    return runs?.map(getRunLabel) ?? [];
  }, [runs]);

  const selectedRunOption = runOptions.find((o) => o.value === selectedBatchId) ?? null;
  const insights = selectedRun?.insights ?? [];
  const filteredInsights = useMemo(() => {
    const filtered = filterInsights(insights, filters);
    return filtered.sort((a, b) => b.content.priorityScore - a.content.priorityScore);
  }, [insights, filters]);

  if (isPolling) {
    return (
      <>
        <DashboardRow>
          <Header
            view={view}
            setView={setView}
            runOptions={runOptions}
            selectedRunOption={selectedRunOption}
            onRunChange={(opt) => setSelectedBatchId(opt?.value ?? '')}
            onGenerateInsights={handleGenerateInsights}
            isGenerating={isGenerating}
          />
        </DashboardRow>
        <DashboardRow>
          <div className='d-flex flex-column w-100 gap-1'>
            <Alert color='info' className='d-flex align-items-center gap-2 mb-3'>
              <i className='fas fa-spinner fa-spin fa-4x me-2' />
              <div>
                <strong>Generation In Progress</strong>
                <div className='text-sm mt-1'>
                  The insights are still being generated. They will appear here as they complete. The page will
                  automatically update when generation finishes. You may safely leave this page and return later to see
                  the results.
                </div>
              </div>
            </Alert>
            <div style={{ position: 'relative', paddingTop: '50px', height: '100px' }}>
              <Loader relative />
            </div>
          </div>
        </DashboardRow>
      </>
    );
  }

  const isLoading = isRunsLoading || isRunFetching;
  const viewDetails = getIntelligenceViewDetails(view);

  return (
    <DashboardRow>
      <div className='d-flex flex-column w-100 gap-1'>
        {isLoading && <Loader />}
        {isGenerating && <BlockingLoader />}

        <Header
          view={view}
          setView={setView}
          runOptions={runOptions}
          selectedRunOption={selectedRunOption}
          onRunChange={(opt) => setSelectedBatchId(opt?.value ?? '')}
          onGenerateInsights={handleGenerateInsights}
          isGenerating={isGenerating}
        />

        <Heading
          title={viewDetails.title}
          subtitle={viewDetails.subtitle}
          colours={
            view === IntelligenceView.Intelligence
              ? { bgFrom: 'var(--theme-BgAccentExtralight)', bgTo: 'var(--theme-DangerExtralight)' }
              : view === IntelligenceView.Priorities
                ? { bgFrom: 'var(--theme-SuccessExtralight)', bgTo: 'var(--theme-AccentExtralight)' }
                : { bgFrom: 'var(--theme-WarningExtralight)', bgTo: 'var(--theme-AccentExtralight)' }
          }
        />
        <InsightsFilters filters={filters} onFilterChange={setFilters} insights={insights} />

        {/* Intelligence View */}
        {view === IntelligenceView.Intelligence && <IntelligenceViewComponent insights={filteredInsights} />}

        {/* Priorities View */}
        {view === IntelligenceView.Priorities && <PrioritiesView insights={filteredInsights} />}

        {/* Recommendations View */}
        {view === IntelligenceView.Recommendations && <RecommendationsView insights={filteredInsights} />}
      </div>
    </DashboardRow>
  );
};
